/**
 * Stone Investment Area Properties
 * 
 * Property grid for area pages with Stone Investment's luxury aesthetic:
 * - Featured properties in the area
 * - View all link
 * - Editorial layout
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import type { AreaProperty } from '@routeroyal/theme-core';

export interface StoneAreaPropertiesProps {
  /**
   * Properties to display
   */
  properties: AreaProperty[];
  /**
   * Area name for heading
   */
  areaName: string;
  /**
   * Link to view all properties
   */
  viewAllLink: string;
  /**
   * Maximum items to show
   */
  maxItems?: number;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneAreaProperties - Property grid for area pages
 */
export function StoneAreaProperties({
  properties,
  areaName,
  viewAllLink,
  maxItems = 3,
  messages = {},
  locale = 'en',
  className = '',
}: StoneAreaPropertiesProps) {
  const displayProperties = properties.slice(0, maxItems);
  
  if (properties.length === 0) {
    return null;
  }
  
  return (
    <section className={`bg-stone-50 py-20 px-6 md:px-12 ${className}`}>
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
              {messages.propertiesIn || 'Properties in'}
            </p>
            <h2 
              className="font-serif text-3xl md:text-4xl text-stone-900"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {areaName}
            </h2>
          </div>
          
          <Link
            href={viewAllLink}
            className="inline-flex items-center gap-3 text-sm font-sans uppercase tracking-[0.15em] text-stone-700 hover:text-stone-900 transition-colors group"
          >
            <span>{messages.viewAll || 'View All Properties'}</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </Link>
        </div>
        
        {/* Properties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {displayProperties.map((property) => (
            <Link 
              key={property.id}
              href={property.slug ? `/${locale}/properties/${property.slug}` : `/${locale}/properties/${property.id}`}
              className="group block"
            >
              <div className="relative aspect-video overflow-hidden bg-stone-200 mb-4">
                {property.image && (
                  <Image
                    src={property.image}
                    alt={property.title}
                    fill
                    className="object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                )}
              </div>
              <div className="border-t border-stone-200 pt-4">
                <h3 className="font-serif text-xl text-stone-900 mb-2 group-hover:italic transition-all">
                  {property.title}
                </h3>
                <p className="text-sm text-stone-600 font-sans mb-2">{property.type}</p>
                <p className="font-serif text-lg text-stone-900">{property.price}</p>
              </div>
            </Link>
          ))}
        </div>
        
        {/* View All Footer (on mobile) */}
        {properties.length > maxItems && (
          <div className="mt-12 text-center md:hidden">
            <Link
              href={viewAllLink}
              className="inline-flex items-center gap-2 px-6 py-3 border border-stone-300 text-sm font-sans uppercase tracking-[0.15em] text-stone-700 hover:border-stone-400 hover:text-stone-900 transition-colors"
            >
              <span>{messages.viewAll || 'View All'} ({properties.length})</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default StoneAreaProperties;
